import type DraggableItem from './DraggableItem';
import ItemPlacement, {
  type ItemInterface,
} from 'components/framework/models/ItemPlacement';
import { type SelectableItemController } from './SelectableItem';
import { sharedDragController } from './controllers/DragController';
import { sharedSelectionController } from 'components/SelectionController/SelectionController';

export type ListLayout = 'grid' | 'horizontal' | 'vertical';

/**
 * Base class for handling common behavior for droppable
 * areas. Inherit from this for your own droppable areas.
 * DropHandlers exist to perform actions when a DraggableItem enters
 * or leaves its scope, or is dropped into a DropHandler's area.
 *
 * A DropHandler is responsible for:
 *
 * - Performing controller actions when an item:
 *   - enters or leaves its scope
 *   - moves within a draggable area
 *   - is dropped into a draggable area
 *
 */
class DropHandler<
  Controller extends SelectableItemController = SelectableItemController
> {
  controller: Controller;

  /**
   * Creates a new DropHandler.
   */
  constructor({ controller }: { controller: Controller }) {
    this.controller = controller;
  }

  get type() {
    return this.constructor.name;
  }

  /**
   * A list of DraggableItem types this DropHandler can handle.
   */
  static get accepts(): string[] {
    return [];
  }

  /**
   * Delegates to the constructor for the list of types accepted by the drop handler.
   */
  get accepts(): string[] {
    return (this.constructor as typeof DropHandler).accepts;
  }

  /**
   * Returns true if future callbacks should be called for
   * draggedItem, false otherwise. Useful to skip handlers when
   * draggedItem is already in the right place.
   */
  canHover({ draggedItem }: { draggedItem: DraggableItem }): boolean {
    return true;
  }

  /**
   * Called when a DraggableItem hovers over a DropHandler. When the drop target is a SimpleDropTarget or OverlayDropTarget,
   * only onHover will be called. When using a PositionedDropTarget, onReorder will be called instead.
   */
  onHover({ draggedItem }: { draggedItem: DraggableItem }) {}

  /**
   * For reorderable items, called when a DraggableItem hovers over part of this DropHandler.
   *
   * Must provide a ItemPlacement, which specifies the relative position of the drag.
   */
  onReorder({
    draggedItem,
    placement,
  }: {
    draggedItem: DraggableItem;
    placement: ItemPlacement;
  }) {}

  /**
   * Called when a DraggableItem is successfully dropped into this DropHandler.
   */
  async onDrop({ draggedItem }: { draggedItem: DraggableItem }) {
    const dropHandler = this;
    const droppedItem = this.prepareDroppedItem(draggedItem);

    draggedItem.onEnd({ dropHandler });

    this.updateSelection([droppedItem]);

    sharedDragController.actionStopDrag();

    // Persist the droppedItem
    const persistedItem = await draggedItem.persistDroppedItem(droppedItem);

    // Now we can persist all changes to the items
    await this.persistDroppedItem({
      draggedItem,
      item: persistedItem,
    });

    this.updateSelection([persistedItem]);
  }

  /**
   * Prepares an item before persisting it.
   */
  prepareDroppedItem(draggedItem: DraggableItem): any {
    return draggedItem.prepareDroppedItem(this);
  }

  /**
   * This is to finalize the drop. The dropped item have been saved, and now any final changes can be
   * made for the drop.
   */
  async persistDroppedItem({
    draggedItem,
    item,
  }: {
    draggedItem: DraggableItem;
    item: any;
  }) {}

  /**
   * Returns the DraggableItem class to use when updating the selection for this drop handler.
   * Used to create new DraggableItems that represent the selected items after dragging from
   * another controller.
   *
   * If not defined, then the selection will be cleared/reset after drop.
   */
  get DraggableItemClass(): typeof DraggableItem | null {
    return null;
  }

  /**
   * Updates the selection state with the dropped items. If the dropped item can be re-selected,
   * then the drop handler will need to know what type of draggable item is now selected.
   */
  updateSelection(droppedItems: any[]) {
    const { DraggableItemClass } = this;

    if (DraggableItemClass) {
      const { controller } = this;

      // We will re-create the selected tiems with the provided DraggableItemClass
      sharedSelectionController.set(
        droppedItems.map(item => new DraggableItemClass({ item, controller }))
      );
    } else {
      // Otherwise, we clear out the selection entirely
      sharedSelectionController.reset();
    }
  }

  /**
   *  Calculates the placement of the dragged item when it's dropped.
   */
  calculatePlacement({
    dragItem,
    listLayout,
    clientOffset,
    hoverBoundingRect,
  }: {
    dragItem: DraggableItem;
    listLayout: ListLayout;
    clientOffset: { x: number; y: number };
    hoverBoundingRect: DOMRect;
  }) {
    if (listLayout === 'grid') {
      return ItemPlacement.atIndex(dragItem.listIndexWithPreviews);
    }
    const hoverPercent =
      listLayout === 'horizontal'
        ? (clientOffset.x - hoverBoundingRect.left) / hoverBoundingRect.width
        : (clientOffset.y - hoverBoundingRect.top) / hoverBoundingRect.height;

    const placementMethod = hoverPercent < 0.5 ? 'before' : 'after';
    return ItemPlacement[placementMethod](dragItem.item as ItemInterface);
  }
}

export default DropHandler;
